import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { formatDateTime, formatRelative } from "../../lib/format";
import type { ApiKey, CreatedApiKey, ListResponse } from "../../lib/types";
import {
  Button,
  CopyButton,
  EmptyState,
  ErrorNotice,
  Field,
  Modal,
  ModePill,
  PageHeader,
  SelectField,
  Sheet,
  SheetHeader,
  Skeleton,
  Table,
  Td,
  Th,
  Tr,
} from "../../components/ui";

export function ApiKeys() {
  const { tenant } = useAuth();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState("");
  const [mode, setMode] = useState<"sandbox" | "live">("sandbox");
  const [created, setCreated] = useState<CreatedApiKey | null>(null);
  const [revoking, setRevoking] = useState<ApiKey | null>(null);

  const keys = useQuery({
    queryKey: ["api-keys"],
    queryFn: () => api.get<ListResponse<ApiKey>>("/api/dashboard/api-keys"),
  });

  const create = useMutation({
    mutationFn: () => api.post<CreatedApiKey>("/api/dashboard/api-keys", { label, mode }),
    onSuccess: (key) => {
      setCreated(key);
      setCreating(false);
      setLabel("");
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.del(`/api/dashboard/api-keys/${id}`),
    onSuccess: () => {
      setRevoking(null);
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
    },
  });

  const rows = keys.data?.data ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Integration"
        title="API keys"
        description="Credentials your server uses to create payments. Keep secrets on your backend — never in a browser or mobile app."
        actions={
          <Button variant="primary" onClick={() => setCreating(true)}>
            Create key
          </Button>
        }
      />

      <Sheet className="settle settle-1 overflow-hidden">
        {keys.isLoading ? (
          <Skeleton />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No API keys yet"
            description="Create a sandbox key to start integrating. You can do this before verification is complete."
            action={
              <Button variant="primary" onClick={() => setCreating(true)}>
                Create your first key
              </Button>
            }
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Label</Th>
                <Th>Mode</Th>
                <Th>Key</Th>
                <Th>Last used</Th>
                <Th>Created</Th>
                <Th align="right" />
              </tr>
            </thead>
            <tbody>
              {rows.map((key) => {
                const revoked = Boolean(key.revoked_at);
                return (
                  <Tr key={key.id} className={revoked ? "opacity-45" : ""}>
                    <Td>{key.label || <span className="text-ink-faint">Untitled</span>}</Td>
                    <Td>
                      <ModePill mode={key.mode} />
                    </Td>
                    <Td mono>
                      <span className="text-ink">{key.key_id}</span>
                      <span className="ml-2 text-ink-faint">····{key.secret_last4}</span>
                    </Td>
                    <Td className="text-[12px] text-ink-muted">
                      {key.last_used_at ? formatRelative(key.last_used_at) : "Never"}
                    </Td>
                    <Td className="text-[12px] text-ink-muted">{formatDateTime(key.created_at)}</Td>
                    <Td align="right">
                      {revoked ? (
                        <span className="text-[12px] text-ink-faint">Revoked</span>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => setRevoking(key)}>
                          Revoke
                        </Button>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Sheet>

      <div className="settle settle-2 mt-6 grid gap-6 lg:grid-cols-2">
        <Sheet className="overflow-hidden">
          <SheetHeader title="How to authenticate" description="Send both headers on every request" />
          <pre className="overflow-x-auto px-5 py-4 font-mono text-[11.5px] leading-relaxed text-ink-muted">
{`curl https://api.bushveld.co.zw/v1/payments \\
  -H "X-API-Key: pk_live_..." \\
  -H "X-API-Secret: sk_live_..." \\
  -H "Idempotency-Key: order-4471" \\
  -H "Content-Type: application/json" \\
  -d '{
    "reference": "order-4471",
    "amount": "125.00",
    "currency": "USD",
    "method": "ecocash",
    "item_name": "Grocery order",
    "payer": { "mobile": "0771234567" }
  }'`}
          </pre>
        </Sheet>

        <Sheet className="overflow-hidden">
          <SheetHeader title="Keeping keys safe" />
          <ul className="space-y-3 px-5 py-4 text-[13px] leading-relaxed text-ink-muted">
            <li className="flex gap-2.5">
              <span className="mt-[9px] h-px w-3 shrink-0 bg-rule-strong" aria-hidden="true" />
              A secret is shown once, at creation. We store only a digest, so we cannot
              recover it for you — create a new key instead.
            </li>
            <li className="flex gap-2.5">
              <span className="mt-[9px] h-px w-3 shrink-0 bg-rule-strong" aria-hidden="true" />
              Secrets belong on your server. A key in browser or mobile code can be read
              by anyone using your app.
            </li>
            <li className="flex gap-2.5">
              <span className="mt-[9px] h-px w-3 shrink-0 bg-rule-strong" aria-hidden="true" />
              Revocation takes effect immediately. If a key may have leaked, revoke it
              first and create a replacement afterwards.
            </li>
            {tenant && !tenant.can_transact_live && (
              <li className="flex gap-2.5 text-brass">
                <span className="mt-[9px] h-px w-3 shrink-0 bg-brass" aria-hidden="true" />
                Live keys become available once your business verification is approved.
              </li>
            )}
          </ul>
        </Sheet>
      </div>

      {/* Create */}
      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="Create an API key"
        description="The secret is shown once and cannot be retrieved afterwards."
        footer={
          <>
            <Button onClick={() => setCreating(false)}>Cancel</Button>
            <Button variant="primary" loading={create.isPending} onClick={() => create.mutate()}>
              Create key
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <ErrorNotice error={create.error} />
          <Field
            label="Label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Production server"
            hint="Something you will recognise later, such as where it is used."
          />
          <SelectField
            label="Mode"
            value={mode}
            onChange={(e) => setMode(e.target.value as "sandbox" | "live")}
            options={[
              { value: "sandbox", label: "Sandbox — simulated payments" },
              ...(tenant?.can_transact_live ? [{ value: "live", label: "Live — real money" }] : []),
            ]}
            hint={
              tenant?.can_transact_live
                ? undefined
                : "Live keys unlock once verification is approved."
            }
          />
        </div>
      </Modal>

      {/* Reveal, once */}
      <Modal
        open={Boolean(created)}
        onClose={() => setCreated(null)}
        title="Your new API key"
        description="Copy the secret now. This is the only time it will be shown."
        footer={
          <Button variant="primary" onClick={() => setCreated(null)}>
            I have stored it safely
          </Button>
        }
      >
        {created && (
          <div className="space-y-4">
            <div className="rounded-md border border-brass/30 bg-brass-wash px-3.5 py-2.5">
              <p className="text-[12.5px] leading-relaxed text-ink">{created.warning}</p>
            </div>

            <div>
              <p className="label mb-1.5">Key ID (public)</p>
              <div className="flex items-center justify-between gap-2 rounded-md border border-rule-strong bg-paper-sunk px-3 py-2">
                <code className="truncate font-mono text-[12px]">{created.key_id}</code>
                <CopyButton value={created.key_id} />
              </div>
            </div>

            <div>
              <p className="label mb-1.5">Secret (keep private)</p>
              <div className="flex items-center justify-between gap-2 rounded-md border border-verdigris/30 bg-verdigris-wash px-3 py-2">
                <code className="truncate font-mono text-[12px] text-verdigris">
                  {created.secret}
                </code>
                <CopyButton value={created.secret} />
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Revoke */}
      <Modal
        open={Boolean(revoking)}
        onClose={() => setRevoking(null)}
        title="Revoke this key?"
        description="Any request using it will start failing immediately. This cannot be undone."
        footer={
          <>
            <Button onClick={() => setRevoking(null)}>Keep it</Button>
            <Button
              variant="danger"
              loading={revoke.isPending}
              onClick={() => revoking && revoke.mutate(revoking.id)}
            >
              Revoke key
            </Button>
          </>
        }
      >
        {revoking && (
          <div className="space-y-3">
            <ErrorNotice error={revoke.error} />
            <p className="text-[13px] text-ink-muted">
              {revoking.label || "This key"} ({revoking.mode}) —{" "}
              <code className="font-mono text-[12px]">{revoking.key_id}</code>
            </p>
            {revoking.mode === "live" && (
              <p className="rounded-md border border-sienna/25 bg-sienna-wash px-3 py-2 text-[12.5px] text-sienna">
                This is a live key. Any production traffic using it will stop working
                the moment you revoke it.
              </p>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
